import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { getToken, clearToken } from './tokenStorage'
import { normalizeError } from './errorNormalizer'

/**
 * Empty base URL means same-origin — nginx on storefront.vegabooking.io proxies /api
 * to Laravel, so there is no CORS and no preflight. VITE_API_BASE_URL only
 * exists as an escape hatch for pointing a branch build at another backend.
 */
const BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

/** Every storefront endpoint lives under this prefix, scoped by vendor slug. */
export const storefrontPath = (slug: string, path: string) =>
  `/api/storefront/v1/${slug}${path.startsWith('/') ? path : `/${path}`}`

const rawBaseQuery = fetchBaseQuery({
  baseUrl: BASE_URL,
  prepareHeaders: (headers, { getState }) => {
    headers.set('Accept', 'application/json')
    const slug = (getState() as { tenant?: { slug: string | null } }).tenant?.slug
    if (slug) {
      const token = getToken(slug)
      if (token) headers.set('Authorization', `Bearer ${token}`)
    }
    return headers
  },
})

/**
 * Unwraps Laravel's { status, message, data } envelope and turns every failure
 * into the same shape, so screens never branch on transport vs. payload errors.
 */
const baseQueryWithEnvelope: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  const result = await rawBaseQuery(args, api, extraOptions)

  if (result.error) {
    if (result.error.status === 401) {
      const slug = (api.getState() as { tenant?: { slug: string | null } }).tenant?.slug
      if (slug) clearToken(slug)
    }
    return { error: normalizeError(result.error) }
  }

  const body = result.data as { status?: boolean; message?: string; data?: unknown } | undefined

  if (body && typeof body === 'object' && 'status' in body) {
    if (body.status === false) {
      return {
        error: normalizeError({
          status: 'CUSTOM_ERROR',
          error: body.message ?? 'Request failed',
          data: body,
        } as FetchBaseQueryError),
      }
    }
    return { data: body.data }
  }

  return { data: result.data }
}

export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithEnvelope,
  tagTypes: [
    'Bootstrap',
    'Category',
    'Service',
    'Branch',
    'Staff',
    'Review',
    'Availability',
    'Booking',
    'Profile',
  ],
  endpoints: () => ({}),
})
